import Box from "@mui/material/Box";
import type {PaperProps} from "@mui/material/Paper";
import {useCallback, useMemo, useState, type ElementType, type ReactNode, type Ref} from "react";

import {projectCardClasses as classes} from "./classes.js";
import {ProjectCardExpansionContext} from "./ProjectCardExpansionContext.js";
import {ProjectCardDescriptionPreview, ProjectCardMetricRow} from "./ProjectCardParts.js";
import {ProjectCardRoot} from "./ProjectCardRoot.js";

export interface ProjectCardProps extends Omit<PaperProps, "children" | "title"> {
    component?: ElementType;
    /** Use the fixed-height layout while the card is collapsed. */
    uniformHeight?: boolean;
    /** Switch layouts off the card's own width (container query) instead of the viewport. */
    widthResponsive?: boolean;
    /** Selection checkbox, absolutely positioned in the card's corner. */
    checkbox?: ReactNode;
    /**
     * The thumbnail area, typically a `ProjectCardThumbnailArea` wrapping the
     * host app's linked thumbnail. Omit it entirely for the compact layout.
     */
    thumbnailArea?: ReactNode;
    /** The linked project name, typically a `ProjectCardTitle`. */
    title: ReactNode;
    /** The project description, typically a `ProjectCardDescription`. */
    description?: ReactNode;
    /**
     * Contents of the author row: owner avatar + handle, contributor avatars,
     * and timestamp.
     */
    authorRow?: ReactNode;
    /** Controls that remain visible after the author content is clamped. */
    authorRowTrailing?: ReactNode;
    /** Contents of the metric row: star control and metric counters. */
    metrics?: ReactNode;
    /** Clamp the metric row to a single line (overflowing metrics hidden). */
    metricsCollapsed?: boolean;
    metricsInnerRef?: Ref<HTMLDivElement>;
    /** Rendered outside the clamped metric row, e.g. a show-more button. */
    metricsTrailing?: ReactNode;
    /** Extra nodes rendered inside the card root, e.g. dialogs and menus. */
    children?: ReactNode;
}

/** Shared project card shell and layout for profile and search surfaces. */
export function ProjectCard(props: ProjectCardProps) {
    const {
        uniformHeight,
        widthResponsive,
        checkbox,
        thumbnailArea,
        title,
        description,
        authorRow,
        authorRowTrailing,
        metrics,
        metricsCollapsed,
        metricsInnerRef,
        metricsTrailing,
        children,
        className,
        ...rootProps
    } = props;

    const [expanded, setIsExpanded] = useState(false);
    const [titleTruncated, setIsTitleTruncated] = useState(false);
    const setExpanded = useCallback((nextExpanded: boolean) => setIsExpanded(nextExpanded), []);
    const setTitleTruncated = useCallback((truncated: boolean) => setIsTitleTruncated(truncated), []);
    const expansionValue = useMemo(
        () => ({expanded, setExpanded, titleTruncated, setTitleTruncated}),
        [expanded, setExpanded, setTitleTruncated, titleTruncated],
    );

    return (
        <ProjectCardExpansionContext.Provider value={expansionValue}>
            <ProjectCardRoot
                component="article"
                elevation={0}
                {...rootProps}
                uniformHeight={uniformHeight}
                expanded={expanded}
                widthResponsive={widthResponsive}
                className={className ? `${classes.cardWrapper} ${className}` : classes.cardWrapper}
            >
                {checkbox}
                <Box className={classes.cardInner}>
                    {thumbnailArea}
                    <Box className={classes.content}>
                        <Box className={classes.topSection}>
                            {title}
                            {description ??
                                (titleTruncated || expanded ? (
                                    <ProjectCardDescriptionPreview text="" previewHeight={uniformHeight ? 40 : 60} />
                                ) : uniformHeight ? (
                                    <Box className={classes.description} />
                                ) : null)}
                            {(authorRow || authorRowTrailing) && (
                                <Box className={classes.authorRow}>
                                    {authorRow && <Box className={classes.authorRowInner}>{authorRow}</Box>}
                                    {authorRowTrailing && (
                                        <Box className={classes.authorRowTrailing}>{authorRowTrailing}</Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                        {metrics && (
                            <ProjectCardMetricRow
                                collapsed={metricsCollapsed}
                                innerRef={metricsInnerRef}
                                trailing={metricsTrailing}
                            >
                                {metrics}
                            </ProjectCardMetricRow>
                        )}
                    </Box>
                </Box>
                {children}
            </ProjectCardRoot>
        </ProjectCardExpansionContext.Provider>
    );
}
